/**
 * Browser-context gamepad emulation installer.
 *
 * This returns a JS string that can run in a real browser (or Playwright page) to:
 * - Patch navigator.getGamepads()
 * - Provide window.__merelsGamepadEmulation API
 *
 * Runtime code can execute this string (Function(script)()).
 * Playwright can inject it via page.addInitScript({ content: script }) or page.evaluate(script).
 */
export function getMerelsGamepadEmulationInstallScript(): string {
  // Keep as pure JS (no TS syntax) because this runs inside the browser.
  return `
(function () {
  if (typeof window === 'undefined') return;
  if (window.__merelsGamepadEmulation && window.__merelsGamepadEmulation.__installed) return;

  var originalGetGamepads = null;
  var isEnabled = false;
  var slotCount = 0;
  var slots = [];

  function makeButtons(count) {
    var buttons = [];
    for (var i = 0; i < count; i++) {
      buttons.push({ pressed: false, touched: false, value: 0 });
    }
    return buttons;
  }

  function ensureSlotCount(count) {
    var next = Math.max(0, count | 0);
    if (next <= slotCount) return;
    for (var i = slotCount; i < next; i++) {
      slots[i] = null;
    }
    slotCount = next;
  }

  function patchedGetGamepads() {
    // Stable array length; null slots for disconnected gamepads.
    return slots.slice(0, slotCount);
  }

  function dispatchGamepadEvent(type, gamepad) {
    // Use CustomEvent for broad compatibility.
    var ev = new CustomEvent(type, { bubbles: true, cancelable: true });
    Object.defineProperty(ev, 'gamepad', {
      value: gamepad,
      writable: false,
      enumerable: true,
      configurable: false
    });
    window.dispatchEvent(ev);
  }

  function makeGamepad(index, id) {
    return {
      index: index,
      connected: true,
      id: String(id || ''),
      mapping: 'standard',
      timestamp: Date.now(),
      buttons: makeButtons(17),
      axes: [0, 0, 0, 0],
    };
  }

  function enable() {
    if (isEnabled) return;
    if (!navigator || typeof navigator.getGamepads !== 'function') return;
    if (!originalGetGamepads) originalGetGamepads = navigator.getGamepads.bind(navigator);
    Object.defineProperty(navigator, 'getGamepads', {
      value: patchedGetGamepads,
      writable: false,
      configurable: true,
    });
    isEnabled = true;
  }

  function disable() {
    if (!isEnabled) return;
    // Fire disconnect events for any connected gamepads.
    for (var i = 0; i < slotCount; i++) {
      var gp = slots[i];
      if (gp && gp.connected) {
        gp.connected = false;
        dispatchGamepadEvent('gamepaddisconnected', gp);
      }
      slots[i] = null;
    }
    if (originalGetGamepads) {
      try {
        Object.defineProperty(navigator, 'getGamepads', {
          value: originalGetGamepads,
          writable: false,
          configurable: true,
        });
      } finally {
        originalGetGamepads = null;
      }
    }
    slotCount = 0;
    isEnabled = false;
  }

  function connect(index, id) {
    enable();
    ensureSlotCount(index + 1);
    var gp = slots[index];
    if (!gp) {
      gp = makeGamepad(index, id);
      slots[index] = gp;
    } else {
      gp.connected = true;
      gp.id = String(id || gp.id || '');
      gp.timestamp = Date.now();
    }
    dispatchGamepadEvent('gamepadconnected', gp);
  }

  function disconnect(index) {
    if (!isEnabled) return;
    if (index < 0 || index >= slotCount) return;
    var gp = slots[index];
    if (!gp) return;
    gp.connected = false;
    dispatchGamepadEvent('gamepaddisconnected', gp);
    slots[index] = null;
  }

  function pulseButton(index, buttonIndex, durationMs) {
    if (!isEnabled) return;
    if (index < 0 || index >= slotCount) return;
    var gp = slots[index];
    if (!gp || !gp.connected) return;
    var b = gp.buttons[buttonIndex];
    if (!b) return;
    b.pressed = true;
    b.value = 1;
    gp.timestamp = Date.now();
    window.setTimeout(function () {
      // If disconnected mid-flight, do nothing.
      if (slots[index] !== gp) return;
      b.pressed = false;
      b.value = 0;
      gp.timestamp = Date.now();
    }, Math.max(0, durationMs || 50));
  }

  function setLeftStick(index, x, y) {
    if (!isEnabled) return;
    if (index < 0 || index >= slotCount) return;
    var gp = slots[index];
    if (!gp || !gp.connected) return;
    gp.axes[0] = Math.max(-1, Math.min(1, x || 0));
    gp.axes[1] = Math.max(-1, Math.min(1, y || 0));
    gp.timestamp = Date.now();
  }

  function resetLeftStick(index) {
    setLeftStick(index, 0, 0);
  }

  window.__merelsGamepadEmulation = {
    __installed: true,
    enable: enable,
    disable: disable,
    ensureSlotCount: ensureSlotCount,
    connect: connect,
    disconnect: disconnect,
    pulseButton: pulseButton,
    setLeftStick: setLeftStick,
    resetLeftStick: resetLeftStick,
  };
})();
`
}


